<template>
  <div class="modal-wrapper">
    <Modal
      :show="show"
      size="sm"
      :close-on-backdrop="!loading"
      :close-on-escape="!loading"
      :show-close-button="!loading"
      :blur-backdrop="false"
      :backdrop-opacity="0"
      @close="handleClose"
    >
      <template #header>
        <div class="header">
          <div class="header-content">
            <div class="icon-wrapper">
              <Icon icon="fluent-mdl2:people-add" class="icon" />
            </div>
            <div class="header-text">
              <h2 class="title">Confirm enrichment</h2>
            </div>
          </div>
        </div>
      </template>

      <div class="body">
        <p class="text">
          You are about to enrich and import
          <strong>{{ contactsCount }} contacts</strong> from your LinkedIn Sales Navigator search.
          This action will add them to your contacts list.
        </p>
      </div>

      <template #footer>
        <div class="footer">
          <Button variant="secondary" size="md" :disabled="loading" @click="handleClose">
            No
          </Button>
          <Button
            variant="primary"
            size="md"
            :loading="loading"
            :disabled="loading"
            @click="$emit('confirm')"
          >
            Yes, Enrich
          </Button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { Modal, Button, Icon } from "@/components/ui";

const props = defineProps<{
  show: boolean;
  contactsCount: number;
  loading?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  confirm: [];
}>();

const handleClose = () => {
  if (!props.loading) {
    emit("close");
  }
};
</script>

<style scoped>
.header {
  padding: 0;
  margin: 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-right: 0;
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--color-primary-light, #f8f7ff);
  border-radius: 10px;
  flex-shrink: 0;
}

.icon {
  font-size: 22px;
  color: var(--color-primary, #3e3efe);
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  padding-top: 2px;
}

.title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
  text-align: left;
}

.body {
  padding: 24px;
}

.text {
  margin: 0;
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.5;
  text-align: left;
}

.text strong {
  color: #111827;
  font-weight: 600;
}

.footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  width: 100%;
}

.modal-wrapper :deep(.ui-modal__backdrop) {
  z-index: 10060;
  align-items: center;
  justify-content: center;
}

.modal-wrapper :deep(.ui-modal__dialog--sm) {
  max-width: 420px;
  z-index: 10061;
}

.modal-wrapper :deep(.ui-modal__content) {
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.2),
    0 4px 16px rgba(0, 0, 0, 0.1);
}

.modal-wrapper :deep(.ui-modal__header) {
  padding: 20px 24px 16px;
  border-bottom: none;
  position: relative;
  padding-bottom: 16px;
}

.modal-wrapper :deep(.ui-modal__body) {
  padding: 0 24px 20px;
}

.modal-wrapper :deep(.ui-modal__footer) {
  padding: 16px 24px 20px;
}

.modal-wrapper :deep(.ui-modal__close) {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: #6b7280;
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
}

.modal-wrapper :deep(.ui-modal__close:disabled),
.modal-wrapper :deep(.ui-modal__close[disabled]) {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.modal-wrapper :deep(.ui-modal__close:hover) {
  background-color: #f3f4f6;
  color: #111827;
}

.modal-wrapper :deep(.ui-modal__close:active) {
  background-color: #e5e7eb;
}
</style>
